import { Button, SelectOptionType, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Col, Form, Row } from 'reactstrap';

import { createSectionRequest } from '../../../api/requests/sectionsMaintainer';
import DisplayError from '../../../components/info/DisplayError';
import { PathsLayouts } from '../../../config/routes';
import { UpdateTeacherOrAssistantRow } from '../types';
import CreateSectionFields from './CreateSectionFields';

export type CreateSectionFormType = {
  period: SelectOptionType;
  faculty: SelectOptionType;
  quota: string;
  campus: SelectOptionType;
  schedules: SelectOptionType | null;
  courses: SelectOptionType | null;
  courseName: string;
  modalityName: string;
  section: string;
  teachers: UpdateTeacherOrAssistantRow[];
  assistants: UpdateTeacherOrAssistantRow[];
};

const CreateSectionForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const prefix = 'sectionsMaintainer.create';
  const [isUnexpectedError, setIsUnexpectedError] = useState<boolean>(false);
  const location = useLocation<{ sectionId?: string | number }>();

  const methods = useForm<CreateSectionFormType>({
    defaultValues: {
      teachers: [
        { value: 0, label: '', isOnline: false, isPresential: false, order: 0 },
      ],
      assistants: [
        { value: 0, label: '', isOnline: false, isPresential: false, order: 0 },
      ],
    },
  });

  const onSubmit = useCallback(
    async (values: CreateSectionFormType) => {
      const {
        period,
        courses,
        section,
        campus,
        teachers,
        quota,
        assistants,
        schedules,
      } = values;

      const { data, error } = await createSectionRequest({
        maximumStudent: +quota,
        campusId: +campus.value,
        professors: teachers.filter((item) => !!item.value),
        assistants: assistants.filter((item) => !!item.value),
        courseCode: courses?.value.toString() || '',
        periodId: +period.value,
        name: section,
        scheduleId: schedules?.value ? +schedules.value : undefined,
      });
      if (data && !error) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`${prefix}.createSuccess`),
        });
        history.push(`${PathsLayouts.sectionsMaintainer}/${data.id}`);
      } else if (error && error.status && error.status === 400) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.duplicatedSectionError`),
        });
      } else {
        setIsUnexpectedError(true);
      }
    },
    [t, setIsUnexpectedError, history],
  );

  useEffect(() => {
    if (location && location.state) console.log(location.state.sectionId);
  }, [location]);

  if (isUnexpectedError) {
    return (
      <div className="mx-3">
        <DisplayError
          title={t('packages.loadErrorTitle')}
          textBody={t('packages.loadErrorBody')}
          retryAction={methods.handleSubmit(onSubmit)}
          loadingAction={methods.formState.isSubmitting}
          insideCard
        />
      </div>
    );
  }

  return (
    <Card className="p-4 mt-2 mx-3">
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <CreateSectionFields />

          <Row className="py-5 justify-content-end pt-5">
            <Col xs={12} lg={3} className="pb-2 order-2 order-lg-1">
              <Button
                type="button"
                outlined
                onClick={() => history.push(PathsLayouts.sectionsMaintainer)}
                text={t(`common.actions.cancel`)}
                disabled={methods.formState.isSubmitting}
                fullwidth
              />
            </Col>
            <Col xs={12} lg={3} className="pb-2 order-1 order-lg-2">
              <Button
                type="submit"
                text={t(`common.actions.saveAndContinue`)}
                loading={methods.formState.isSubmitting}
                fullwidth
              />
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </Card>
  );
};

export default CreateSectionForm;
